import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import { useSiteConfig } from "../contexts/SiteConfigContext";

const NotFound = lazy(() => import("../pages/NotFound"));
const Home = lazy(() => import("../pages/home/page"));
const FeederPage = lazy(() => import("../pages/feeder/page"));
const PetProfilePage = lazy(() => import("../pages/pet-profile/page"));
const RescuePage = lazy(() => import("../pages/rescue/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const LoginPage = lazy(() => import("../pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("../pages/auth/RegisterPage"));
const MyPetsPage = lazy(() => import("../pages/my-pets/page"));
const CommunityPage = lazy(() => import("../pages/community/page"));
const AdminPage = lazy(() => import("../pages/admin/page"));
const UserProfilePage = lazy(() => import("../pages/user-profile/page"));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-400 border-t-transparent" />
  </div>
);

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

/** 页面隐藏时重定向到首页 */
function PageVisibilityGuard({ pageKey, children }: { pageKey: string; children: React.ReactNode }) {
  const { isVisible, loading } = useSiteConfig();
  if (loading) return <PageLoader />;
  if (!isVisible(pageKey)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const routes: RouteObject[] = [
  { path: "/", element: withSuspense(<Home />) },
  {
    path: "/products/smart-feeder",
    element: (
      <PageVisibilityGuard pageKey="feeder">
        {withSuspense(<FeederPage />)}
      </PageVisibilityGuard>
    ),
  },
  { path: "/pets/:id", element: withSuspense(<PetProfilePage />) },
  {
    path: "/rescue",
    element: (
      <PageVisibilityGuard pageKey="rescue">
        {withSuspense(<RescuePage />)}
      </PageVisibilityGuard>
    ),
  },
  {
    path: "/about",
    element: (
      <PageVisibilityGuard pageKey="about">
        {withSuspense(<AboutPage />)}
      </PageVisibilityGuard>
    ),
  },
  { path: "/login", element: withSuspense(<LoginPage />) },
  { path: "/register", element: withSuspense(<RegisterPage />) },
  {
    path: "/my-pets",
    element: (
      <ProtectedRoute>
        {withSuspense(<MyPetsPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: "/community",
    element: (
      <PageVisibilityGuard pageKey="community">
        {withSuspense(<CommunityPage />)}
      </PageVisibilityGuard>
    ),
  },
  { path: "/user/:id", element: withSuspense(<UserProfilePage />) },
  {
    path: "/admin",
    element: (
      <ProtectedRoute requireAdmin>
        {withSuspense(<AdminPage />)}
      </ProtectedRoute>
    ),
  },
  { path: "*", element: withSuspense(<NotFound />) },
];

export default routes;
